// Angular + External
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, ReplaySubject, Subject } from "rxjs";

import { Idle, ReportMode } from "../../shared/idle/idle";

// Models
import { User } from "../models/user.model";

import {
  BfxUIConfiguration,
  SessionType,
} from "../../../shared/auth-services/system-config/model/config.models";

// Services
import { BfxConfigService } from "../../../shared/auth-services/system-config/bfx-config.service";
import { GoldenLayoutService } from "../../golden-layout/golden-layout-service";
import {
  ActiveUserProfilePromiseService,
  BFXNotificationEventType,
  BFXNotificationType,
  LogonResp,
  NotificationService,
  ProfileSettingKeys,
} from "shared/cubiq-services";
import { UserAuthPromiseService } from "../../../shared/cubiq-services/auth/impl/UserAuthPromiseService";

export const IDLE_TIMEOUT_THRESHOLD_SEC: number = 60;
export const IDLE_TIMEOUT_THRESHOLD_MS: number = 1000 * IDLE_TIMEOUT_THRESHOLD_SEC;
export const DEFAULT_IDLE_TIMEOUT: number = 1000 * 60 * 15;
export const MIN_NOTIFICATION_DURATION: number = 10000;
const DEFAULT_IDLE_CHECK_INTERVAL: number = 10000;
const DEFAULT_IDLE_CHECK_TIMEOUT: number = 200;

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";
import { BfxAppAdminLauncherService } from "./bfx-app-admin-launcher.service";

import {
  ConnectionStatusPromiseService,
  ConnectionStatus,
  ConnectionStatusState,
  CubiqBrokerClient,
  CUBIQ_BROKER,
} from "shared/broker-client-services";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";

// TODO there's a mistake in typescript lib
declare interface IdleDeadline {
  readonly didTimeout: boolean;
  timeRemaining(): DOMHighResTimeStamp;
}
declare interface IdleRequestCallback {
  (deadline: IdleDeadline): void;
}
declare interface IdleRequestOptions {
  timeout?: number;
}
declare function requestIdleCallback(
  callback: IdleRequestCallback,
  options?: IdleRequestOptions,
): NodeJS.Timeout;
declare function cancelIdleCallback(id: NodeJS.Timeout): NodeJS.Timeout;

interface IdleDetector {
  addEventListener(
    type: "change",
    listener: (
      this: IdleDetector,
      ev: { userState: "active" | "idle"; screenState: "locked" | "unlocked" },
    ) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;
  start(options: { threshold: number; signal: AbortSignal }): Promise<void>;
  screenState: "locked" | "unlocked";
  userState: "active" | "idle";
}

declare const IdleDetector: {
  new (): IdleDetector;
  requestPermission(): Promise<"granted" | "denied">;
};

const INITIAL_IDLE = 0;
const ACTIVE = -1;

if (module.hot) {
  module.hot.accept();
}

@Injectable({ providedIn: "root" })
export class BfxAuthService implements OnDestroy {
  /**********************************************************************/
  /* DATA */
  private authenticated: boolean = false;
  private config: BfxUIConfiguration = null;
  private username: string;

  private sessionEndAction: NodeJS.Timeout = null;
  private sessionEndIdleCallback: NodeJS.Timeout = null;
  private sessionEndIdleTimeout: NodeJS.Timeout;
  private sessionEndNotification: NodeJS.Timeout = null;

  private idleCount: number = 0;
  private idleCountMax: number = 1;

  private _authStatusSource = new BehaviorSubject<boolean>(false);
  authStatus$ = this._authStatusSource.asObservable();

  private _hmrPrime = new ReplaySubject<void>(1);
  hmrPrime$ = this._hmrPrime.asObservable();

  protected changePasswordSubject = new Subject<BfxDialogData>();
  public changePassword$ = this.changePasswordSubject.asObservable();

  private isIdle: boolean = false;
  private visibilityListener;
  private configPromise;
  private isConnected: boolean;
  private connectionEventSubscription: any;

  private ssoContextPath: string = null;

  private idleController: AbortController;
  private idleDetector: Partial<IdleDetector>;
  private idleTimestamp: number = -1;
  private fallbackIdle: Idle;

  constructor(
    @Inject(CUBIQ_BROKER) public broker: CubiqBrokerClient,
    private _router: Router,
    private _configService: BfxConfigService,
    private _notifyService: NotificationService,
    private _goldenLayoutService: GoldenLayoutService,
    protected _userAuthService: UserAuthPromiseService,
    private _activeUserService: ActiveUserProfilePromiseService,
    private _adminAppLauncherService: BfxAppAdminLauncherService,
    private _connectionStatusService: ConnectionStatusPromiseService,
  ) {
    this.configPromise = this._configService.getUIConfig();

    this.configPromise.then(
      (config) => {
        this.config = config;

        if (
          this.config != null &&
          this.config.session != null &&
          this.config.session.sessionTimeout > DEFAULT_IDLE_CHECK_INTERVAL
        ) {
          this.idleCountMax = Math.max(
            1,
            this.config.session.sessionTimeout / DEFAULT_IDLE_CHECK_INTERVAL,
          );
        }

        // update local debug
        logger.out.debug(`${this.constructor.name}()= Configuration: %O`, config);
      },
      (err) => logger.out.error(`${this.constructor.name}()= PROMISE ERROR:`, err),
    );

    this.connectionEventSubscription = this._connectionStatusService
      .getConnectionEvents()
      .subscribe((event: ConnectionStatus) => {
        //
        switch (event.getStatus()) {
          case ConnectionStatusState.CONNECTED: {
            this.isConnected = true;
            if (
              module.hot &&
              module?.hot?.data?.user != null &&
              typeof module?.hot?.data?.user === "object"
            ) {
              const user = module.hot.data.user;
              console.log("HMR RELOG allow  %O", module.hot);
              this.login(user);
            } else if (module.hot) {
              console.log("HMR RELOG deny %O", module.hot);
              this._router.navigate(["/"]);
            }
            break;
          }
          default: {
            this.isConnected = false;
            if (module.hot) {
              console.log("HMR RELOG disconnect %O", module.hot);
              this._router.navigate(["/"]);
            }
            break;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.removeListeners();

    if (this.connectionEventSubscription != null) {
      this.connectionEventSubscription.unsubscribe();
      this.connectionEventSubscription = null;
    }
  }

  public login(user: User): Promise<LogonResp> {
    logger.out.info(`${this.constructor.name}.login()`);

    return new Promise((res, _) => {
      this.configPromise.then((_) => {
        this._userAuthService
          .requestLogin(user, this.config.general.loginTimeout, user.isSSO())
          .then(
            (result) => {
              if (result.isSuccess && this.isLimitSessionType()) {
                this.scheduleSessionEnd(
                  this.config.session.sessionDuration,
                  this.config.session.showNotification,
                );
              }

              if (result.isSuccess) {
                let sso: boolean = user.isSSO();
                this.ssoContextPath = window.document.body.getAttribute("cp");

                logger.out.info(
                  `AuthService: Logged in ${sso ? "with" : "without"} SSO` +
                    `, context path: ${this.ssoContextPath}`,
                );

                if (sso) {
                  NativeDesktopSupport.removeMainWindowFrame();
                }

                if (this.ssoContextPath != null) {
                  window.document.body.removeAttribute("cp");
                }
              }

              if (module.hot) {
                const hmrUser = new User(user.username, `__hmr_reload`);
                const disposeFn = (data) => {
                  console.log(`HMR Store user on dispose`);
                  data.user = hmrUser;
                  return;
                };
                console.log(`HMR Store username ${hmrUser.username}`);

                if (module.hot.data == null) {
                  module.hot.data = { user: hmrUser };
                } else {
                  module.hot.data["user"] = hmrUser;
                }
                module.hot.accept((err, ...acceptErrArgs) => {
                  let { moduleId, module } = (acceptErrArgs as any[])[0];
                  logger.out.info(`HMR accepting reload for moduleId: ${moduleId} after error;`);
                  logger.out.info(err);
                  module.hot.data.user = hmrUser;
                  module.hot.accept();
                  module.hot.dispose(disposeFn);
                });
                module.hot.dispose(disposeFn);
              }

              this.username = user.getUsername();
              this.setAuthenticated(result.isSuccess);
              res(result);
            },
            (_) => {
              res(new LogonResp(false));
            },
          );
      });
    });
  }

  public logout(
    desktopGoToLogin: boolean = false,
    callback: (willSaveLogin: boolean) => void = null,
  ): void {
    logger.out.info(`${this.constructor.name}.logout( desktopGoToLogin:${desktopGoToLogin})`);

    let shouldSave: boolean = this.isConnected;

    this._activeUserService
      .getActiveUserData()
      .then((activeUser) => {
        if (
          activeUser.profileSettings &&
          activeUser.profileSettings.has(ProfileSettingKeys.userSaveProfileOnLogout)
        ) {
          let saveProfileOnLogoutStr: string = activeUser.profileSettings.get(
            ProfileSettingKeys.userSaveProfileOnLogout,
          );
          if (saveProfileOnLogoutStr === "false") {
            shouldSave = false;
          }
        }
      })
      .catch((error) => {
        logger.out.error(
          `${this.constructor.name}: Failed to obtain active user data before logout, reason ${error}`,
        );
      })
      .finally(() => {
        this.logoutAndSave(shouldSave, desktopGoToLogin);
        if (callback != null) {
          callback(shouldSave);
        }
      });
  }

  private logoutAndSave(shouldSave: boolean, desktopGoToLogin: boolean) {
    let promise: Promise<void> = Promise.resolve();

    logger.out.info(
      `${this.constructor.name}.logoutAndSave(shouldSave:${shouldSave} desktopGoToLogin:${desktopGoToLogin})`,
    );

    this.clearSessionEnd();
    this.removeListeners();

    if (shouldSave) {
      logger.out.info(`${this.constructor.name}: Save on logout enabled, saving profile ...`);
      promise = this._goldenLayoutService.saveProfileConfig();
    } else {
      logger.out.info(
        `${this.constructor.name}: Save on logout disabled, skipping save profile ...`,
      );
    }

    promise.then(
      () => {
        this.setAuthenticated(false);
        if (!NativeDesktopSupport.isNativeDesktopContainer()) {
          this._adminAppLauncherService.closeAdminWindow();
          this._router.navigate(["logout"]);
          this.broker.disconnect();
        }
        this._userAuthService.disconnectOnLogout().then(() => {
          logger.out.info("BfxAuthService: logout() -> disconnectOnLogout()");
          if (desktopGoToLogin) {
            NativeDesktopSupport.logout(this._router, true);
          } else {
            NativeDesktopSupport.exit();
          }
        });
      },
      (err) => {
        NativeDesktopSupport.prepareLogout();
        logger.out.error("BfxAuthService: logout() -> logout() -> saveProfileConfig " + err);
        NativeDesktopSupport.exit();
      },
    );
  }

  public forceLogout(): void {
    logger.out.info(`${this.constructor.name}.forceLogout()`);
    this.clearSessionEnd();
    this.removeListeners();
    this.setAuthenticated(false);
    if (!NativeDesktopSupport.isNativeDesktopContainer()) {
      this._router.navigate(["logout"]);
      this.broker.disconnect();
    }
    this._userAuthService.disconnectOnLogout().then(() => {
      NativeDesktopSupport.logout(this._router, true);
      logger.out.info("BfxAuthService: logout() -> disconnectOnLogout()");
    });
  }

  public restartLogout(): void {
    logger.out.info(`${this.constructor.name}.restartLogout()`);
    this.clearSessionEnd();
    this.removeListeners();
    this.setAuthenticated(false);
    if (!NativeDesktopSupport.isNativeDesktopContainer()) {
      this._router.navigate(["restart-logout"]);
    }
    this._userAuthService.disconnectOnLogout().then(() => {
      NativeDesktopSupport.logout(this._router, true);
      logger.out.info("BfxAuthService: logout() -> disconnectOnLogout()");
    });
  }

  public notAuthorizedLogout(): void {
    logger.out.info(`${this.constructor.name}.notAuthorizedLogout()`);
    this.clearSessionEnd();
    this.removeListeners();
    this.setAuthenticated(false);
    this._router.navigate(["not-authorized"]);
  }

  public isAuthenticated(): boolean {
    return this.authenticated;
  }

  public getUsername(): string {
    return this.username;
  }
  /**
   * Applies to SessionType.Visibility
   */
  private processIdleChange(idleSince: number = ACTIVE) {
    let sessionTimeRemaining: number = DEFAULT_IDLE_TIMEOUT;
    let showNotification = this.config.session.showNotification;
    // load session time-out
    if (
      this.config != null &&
      this.config.session != null &&
      this.config.session.sessionTimeout >= IDLE_TIMEOUT_THRESHOLD_MS
    ) {
      sessionTimeRemaining = this.config.session.sessionTimeout;
    }

    if (
      NativeDesktopSupport.isNativeDesktopContainer() &&
      NativeDesktopSupport.cmdLineArguments.sessiontimeout != null
    ) {
      sessionTimeRemaining = parseInt(NativeDesktopSupport.cmdLineArguments.sessiontimeout);
    }
    if (idleSince > sessionTimeRemaining) {
      if (this.config.session.showNotification) {
        sessionTimeRemaining = this.config.session.notificationOffset;
      } else {
        sessionTimeRemaining = 0;
        showNotification = false;
      }
    } else if (idleSince > 0) {
      sessionTimeRemaining = sessionTimeRemaining - idleSince;
    }
    if (this.isIdle) {
      if (this.sessionEndAction === null) {
        this.scheduleSessionEnd(sessionTimeRemaining, showNotification);
      }
    } else {
      this.clearSessionEnd();
    }
  }
  /**********************************************************************/
  /* PRIVATE METHODS */
  private async attachListeners() {
    logger.out.info(`AuthService attaching listeners`);
    if (this.isVisibilitySessionType() && "IdleDetector" in window) {
      try {
        const controller = new AbortController();
        const signal = controller.signal;
        const idleDetector = new IdleDetector();
        const perm = await IdleDetector.requestPermission();

        if (perm != "granted") {
          logger.out.error("IdleDetector is not permitted by user");
          this.attachFallbackListeners();
          return;
        } else {
          Idle.superseded = true;
        }

        this.idleDetector = idleDetector;

        idleDetector.addEventListener("change", this.onIdleStateChanged.bind(this));

        await idleDetector.start({
          threshold: IDLE_TIMEOUT_THRESHOLD_MS,
          signal,
        });
        this.idleDetector = idleDetector;
        this.idleController = controller;
        logger.out.info(`${this.constructor.name}.attachListeners() IdleDetector is active.`);
      } catch (err) {
        // Deal with initialization errors like permission denied,
        // running outside of top-level frame, etc.
        logger.out.error(err.name, err.message);
        logger.out.info(`${this.constructor.name}.attachListeners() IdleDetector is not active.`);
      }
    } else if (this.isVisibilitySessionType()) {
      logger.out.error("IdleDetector is not available");
      this.attachFallbackListeners();
    }

    if (this.isVisibilitySessionType() && !NativeDesktopSupport.isNativeDesktopContainer()) {
      logger.out.info(`${this.constructor.name}: Attaching visibilty change idle detector`);
      this.visibilityListener = this.onVisibilityChanged.bind(this);
      document.addEventListener("visibilitychange", this.visibilityListener, false);
    }
  }

  attachFallbackListeners() {
    const idle = new Idle({
      //if any more options are customized please add in Idle.destroy() -dc
      activityReportInSec: IDLE_TIMEOUT_THRESHOLD_SEC,
      activityReportMode: ReportMode.Standard,
      onActivity: () => {
        this.idleDetector = { userState: "active", screenState: "unlocked" };
        this.onIdleStateChanged();
      },
      onReportUserIsActive: () => {
        this.idleDetector = { userState: "active", screenState: "unlocked" };
        this.onIdleStateChanged();
      },
      onReportUserIsIdle: () => {
        this.idleDetector = { userState: "idle", screenState: "unlocked" };
        this.onIdleStateChanged();
      },
    });

    this.fallbackIdle = idle;

    logger.out.warn(`${this.constructor.name}.attachFallbackListeners() Idle polyfill is active.`);
  }

  private removeListeners(): void {
    logger.out.info(`AuthService removing listeners`);

    if (this.visibilityListener != null) {
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.visibilityListener = null;
    }

    if (this.idleController != null) {
      this.idleController.abort();
      this.idleController = null;
    }

    if (this.fallbackIdle != null) {
      //if any more options are customized please add here -dc
      this.fallbackIdle.destroy();
      this.fallbackIdle = null;
    }
  }

  private clearSessionEnd(): void {
    if (this.sessionEndAction != null) {
      clearTimeout(this.sessionEndAction);
      this.sessionEndAction = null;
    }
    if (this.sessionEndNotification != null) {
      clearTimeout(this.sessionEndNotification);
      this.sessionEndNotification = null;
    }

    if (this.fallbackIdle === null) {
      logger.out.info(`${this.constructor.name}.clearSessionEnd()`);
    }
  }

  public scheduleSessionEnd(sessionTimeRemaining: number, notification: boolean): void {
    this.clearSessionEnd();
    logger.out.info(
      `${this.constructor.name}.scheduleSessionEnd() set for ${sessionTimeRemaining}ms notification:${notification}`,
    );
    const sessionEndAction = setTimeout(() => {
      logger.out.info(
        `AuthService running sessionEndAction ` +
          `due to sessionType limit sessionTimeRemaining:${sessionTimeRemaining} notification:${notification}`,
      );
      this.logout(NativeDesktopSupport.isNativeDesktopContainer());
    }, sessionTimeRemaining);

    this.sessionEndAction = sessionEndAction;

    if (notification) {
      let notificationOffset =
        sessionTimeRemaining >= MIN_NOTIFICATION_DURATION
          ? sessionTimeRemaining - MIN_NOTIFICATION_DURATION
          : 0;
      let msg =
        "Your session will expire in " +
        (sessionTimeRemaining - notificationOffset) / 1000 +
        " seconds";
      this.sessionEndNotification = setTimeout(() => {
        logger.out.warn(`AuthService Session Expiration ${msg}`);

        let eventData = this._notifyService.simpleMessageToNotification(
          msg,
          BFXNotificationType.SESSION_END,
        );
        this._notifyService.fireNotificationEvent(eventData, BFXNotificationEventType.SESSION_END);
      }, notificationOffset);
    }
  }

  private isLimitSessionType(): boolean {
    if (this.config != null && this.config.session != null) {
      return this.config.session.sessionType === SessionType.LIMIT;
    }

    return false;
  }

  private isVisibilitySessionType(): boolean {
    if (this.config != null && this.config.session != null) {
      return this.config.session.sessionType === SessionType.VISIBILITY;
    }

    return false;
  }

  private onIdleStateChanged(): void {
    const timestamp = new Date().valueOf();
    const wasIdle = this.isIdle;

    this.isIdle = this.idleDetector.userState === "idle";

    if (this.isIdle && this.idleTimestamp > 0) {
      this.processIdleChange(timestamp - this.idleTimestamp);
    } else if (this.isIdle && this.idleTimestamp === -1) {
      this.idleTimestamp = timestamp;
      this.processIdleChange(INITIAL_IDLE);
    } else if (!this.isIdle) {
      this.idleTimestamp = -1;
      this.processIdleChange(ACTIVE);
    }

    if (wasIdle !== this.isIdle) {
      logger.out.info(
        `${this.constructor.name}.onIdleStateChanged(): session idle: ` +
          `userState: ${this.idleDetector.userState} ` +
          `screenState: ${this.idleDetector.screenState} ` +
          `${
            this.idleTimestamp === -1 ? `active` : `idle since: ${new Date(this.idleTimestamp)}.`
          }`,
      );
    }
  }

  private setAuthenticated(status: boolean): void {
    this.authenticated = status;

    if (this.authenticated) {
      this.attachListeners();
    } else {
      this.removeListeners();
    }

    this._authStatusSource.next(status);
  }

  public getSSOContextPath(): string {
    return this.ssoContextPath;
  }

  public requestChangePassword(): Promise<boolean> {
    return new Promise((res) => {
      // Payload has callback to fulfill the promise
      const payload = {
        callback: (success: boolean) => {
          res(success);
        },
      };
      const dialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_CHANGE_PASSWORD_OPEN,
        null,
        null,
        payload,
      );
      this.changePasswordSubject.next(dialogData);
    });
  }

  private clearIdleCheck(resetIdleCount: boolean = true) {
    logger.out.info(`${this.constructor.name}: Clearing idle check with reset ${resetIdleCount}`);

    if (this.sessionEndIdleCallback != null) {
      cancelIdleCallback(this.sessionEndIdleCallback);
      this.sessionEndIdleCallback = null;
    }
    if (this.sessionEndIdleTimeout != null) {
      clearTimeout(this.sessionEndIdleTimeout);
      this.sessionEndIdleTimeout = null;
    }

    if (resetIdleCount) {
      this.idleCount = 0;
    }
  }

  /**
   * admin may lose visibility without a onVisibilityChanged() call
   * so confirm both are visibile with the same cadance as sessionTimeout
   *
   * preempted by visibilitychange
   *
   * @param sessionTimeRemaining
   */
  private scheduleIdleCheck(idleInterval: number) {
    this.clearIdleCheck(false);

    this.sessionEndIdleTimeout = setTimeout(() => {
      this.sessionEndIdleCallback = requestIdleCallback(
        (deadline) => {
          logger.out.debug(
            `AuthService.scheduleIdleCheck() check ` +
              `idleCount:${this.idleCount} idleCountMax:${this.idleCountMax} ` +
              `deadline:${deadline.didTimeout} `,
          );

          this.isIdle =
            document.visibilityState === "hidden" &&
            this._adminAppLauncherService.isAdminWindowHidden();

          if (this.isIdle) {
            this.idleCount = this.idleCount + 1;
          } else {
            this.idleCount = 0;
            this.clearSessionEnd();
          }

          if (this.isIdle && this.idleCount == this.idleCountMax) {
            logger.out.info(
              `AuthService.scheduleIdleCheck() max idle count reached ${this.idleCountMax} ending session`,
            );

            this.scheduleSessionEnd(0, false);
          } else {
            this.scheduleIdleCheck(idleInterval);
          }
        },
        {
          // while has the property of firing when user goes idle
          // it isn't 1:1 related to visibility
          // use a much smaller value than idleInteravl
          timeout: DEFAULT_IDLE_CHECK_TIMEOUT,
        },
      );
    }, idleInterval);
  }

  private onVisibilityChanged(_: Event): void {
    this.isIdle =
      document.visibilityState === "hidden" && this._adminAppLauncherService.isAdminWindowHidden();
    if (!this.isIdle) {
      this.clearIdleCheck();
    }
    this.processIdleChange();
    logger.out.info("AuthService.onIdleStateChanged(): Browser session idle: " + this.isIdle);
  }
}
